import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from "react-router-dom";

import { Button } from "components/ui/button";
import { Activity, CircleSmall, Monitor, Radio, Wifi, Loader2, Copy, Check, X, AlertTriangle } from 'lucide-react';
import { Switch } from "components/ui/switch";
import { Slider } from 'components/ui/slider';
import { Progress } from 'components/ui/progress';

import PageTitle from '../components/generic/PageTitle';
import Breadcrumbs from 'components/generic/Breadcrumbs';
import Select from "components/generic/Select";
import LivestreamPlayer from 'components/livestream/LivestreamPlayer';
import PlatformConnectionModal from 'components/livestream/PlatformConnectionModal';

import { platformNames } from 'data/staticData';
import {
  getProjectDetails,
  createOrGetStream,
  startMediaLive,
  getMediaLiveDetails,
  getStreamStatus,
  stopStream,
  getDestinations,
  addDestination,
  updateDestination,
  deleteDestination,
  getStreamHealth,
} from 'services/livestreamService';

const streamQualityOptions = [
  { value: "sd", label: "SD (480p)" },
  { value: "hd", label: "HD (720p)" },
  { value: "full_hd", label: "Full HD (1080p)" },
  { value: "uhd", label: "Ultra HD (4K)" },
];

const parseRtmpUrl = (url) => {
  if (!url) return { server: '', key: '' };
  const lastSlashIndex = url.lastIndexOf('/');
  if (lastSlashIndex === -1) return { server: url, key: '' };

  return {
    server: url.substring(0, lastSlashIndex),
    key: url.substring(lastSlashIndex + 1)
  };
};

const ManageLivestreamPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  // State management
  const [loading, setLoading] = useState(true);
  const [goLiveLoading, setGoLiveLoading] = useState(false);
  const [stopLoading, setStopLoading] = useState(false);
  const [error, setError] = useState(null);

  // Data state
  const [project, setProject] = useState(null);
  const [stream, setStream] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [streamStatus, setStreamStatus] = useState(null);
  const [streamHealth, setStreamHealth] = useState(null);
  const [mediaLiveDetails, setMediaLiveDetails] = useState(null);

  // UI state
  const [selectedQuality, setSelectedQuality] = useState("");
  const [sliderValue, setSliderValue] = useState([85]);
  const [cameraOn, setCameraOn] = useState(false);
  const [microphoneOn, setMicrophoneOn] = useState(false);
  const [showPlatformModal, setShowPlatformModal] = useState(false);
  const [editingDestination, setEditingDestination] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Status polling interval
  const [pollingInterval, setPollingInterval] = useState(null);

  // Load initial data
  useEffect(() => {
    if (!projectId) {
      setError("Project ID is missing");
      setLoading(false);
      return;
    }
    loadInitialData();
  }, [projectId]);

  // Start polling when stream exists (more aggressive polling)
  useEffect(() => {
    if (!stream?.stream_id) return;

    // Always poll when we have a stream - check status continuously
    if (!pollingInterval) {
      console.log('🔄 Starting status polling...');
      const interval = setInterval(() => {
        pollStreamStatus();
      }, 3000); // Poll every 3 seconds
      setPollingInterval(interval);

      // Also poll immediately
      pollStreamStatus();
    }

    return () => {
      if (pollingInterval) {
        console.log('⏹️ Stopping status polling');
        clearInterval(pollingInterval);
      }
    };
  }, [stream?.stream_id]);

  const loadInitialData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Step 1: Get project details
      const projectRes = await getProjectDetails(projectId);
      if (!projectRes.success) {
        throw new Error(projectRes.error || 'Failed to load project');
      }
      setProject(projectRes.data);

      // Step 2: Create or get stream
      const streamRes = await createOrGetStream(projectId);
      if (!streamRes.success) {
        throw new Error(streamRes.error || 'Failed to initialize stream');
      }
      setStream(streamRes.data);

      const streamId = streamRes.data.stream_id;

      // Step 3: Get destinations
      const destRes = await getDestinations(streamId);
      if (destRes.success) {
        setDestinations(destRes.data || []);
      }

      // Step 4: Get initial status
      const statusRes = await getStreamStatus(streamId);
      if (statusRes.success) {
        setStreamStatus(statusRes.data);
      }

    } catch (err) {
      console.error('Initial data load error:', err);
      setError(err.message || 'Failed to load livestream data');
    } finally {
      setLoading(false);
    }
  };

  const pollStreamStatus = async () => {
    if (!stream?.stream_id) return;

    try {
      const [statusRes, healthRes, destRes] = await Promise.all([
        getStreamStatus(stream.stream_id),
        getStreamHealth(stream.stream_id),
        getDestinations(stream.stream_id)
      ]);

      if (destRes.success) {
        setDestinations(destRes.data || []);
      }

      if (healthRes.success) {
        setStreamHealth(healthRes.data);
      }

      if (statusRes.success) {
        const newStatus = statusRes.data;
        setStreamStatus(newStatus);

        // Auto-fetch MediaLive details when MediaLive is RUNNING but we don't have them yet
        if (newStatus?.medialive?.state === 'RUNNING' && !mediaLiveDetails) {
          console.log('MediaLive detected as RUNNING, fetching details...');
          const mlDetails = await getMediaLiveDetails(stream.stream_id);
          if (mlDetails.success) {
            setMediaLiveDetails(mlDetails.data);
            console.log('MediaLive details fetched successfully');
          }
        }

        // Clear MediaLive details when stopped
        if (newStatus?.medialive?.state === 'IDLE' && mediaLiveDetails) {
          console.log('MediaLive stopped, clearing details');
          setMediaLiveDetails(null);
        }
      }
    } catch (err) {
      console.error('Status polling error:', err);
    }
  };

  const handleGoLive = async () => {
    if (!stream?.stream_id) {
      alert("Stream ID not found. Please refresh the page.");
      return;
    }

    setGoLiveLoading(true);

    // Retry logic for intermittent network/API errors
    const maxRetries = 3;
    let lastError = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🚀 Attempting to start MediaLive (attempt ${attempt}/${maxRetries})...`);

        const response = await startMediaLive(stream.stream_id);

        if (response.success) {
          console.log('✅ MediaLive started successfully');

          // Fetch MediaLive details to show OBS settings
          const mlDetails = await getMediaLiveDetails(stream.stream_id);
          if (mlDetails.success) {
            setMediaLiveDetails(mlDetails.data);
            console.log('✅ MediaLive details fetched');
          }

          // Refresh status immediately
          await pollStreamStatus();

          alert("MediaLive started successfully! Configure OBS with the settings below and start streaming.");
          setGoLiveLoading(false);
          return; // Success - exit
        } else {
          lastError = response.error || "Failed to start MediaLive";
          console.error(`❌ Attempt ${attempt} failed:`, lastError);

          // If it's not a network error, don't retry
          if (!lastError.toLowerCase().includes('network') && !lastError.toLowerCase().includes('timeout')) {
            break;
          }
        }
      } catch (error) {
        lastError = error.message || "Network error occurred";
        console.error(`❌ Attempt ${attempt} caught error:`, error);

        // Wait before retry with exponential backoff
        if (attempt < maxRetries) {
          const waitTime = 1000 * attempt; // 1s, 2s, 3s
          console.log(`⏳ Waiting ${waitTime}ms before retry...`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
      }
    }

    // All retries failed
    setGoLiveLoading(false);
    alert(`Failed to start MediaLive: ${lastError}\n\nPlease try again.`);
  };

  const handleStopStream = async () => {
    if (!stream?.stream_id) return;

    const confirmed = window.confirm(
      "Are you sure you want to stop the stream? This will end the broadcast and clean up MediaLive resources."
    );
    if (!confirmed) return;

    setStopLoading(true);
    try {
      const response = await stopStream(stream.stream_id);
      if (response.success) {
        setMediaLiveDetails(null);
        await pollStreamStatus();
        alert("Stream stopped successfully.");
      } else {
        alert(response.error || "Failed to stop stream.");
      }
    } catch (error) {
      console.error("Stop Stream Error:", error);
      alert("An unexpected error occurred while stopping stream.");
    } finally {
      setStopLoading(false);
    }
  };

  const handleSaveDestination = async ({ platform, rtmpUrl, destinationId }) => {
    setModalLoading(true);
    try {
      let response;
      if (destinationId) {
        // Update existing
        response = await updateDestination(destinationId, rtmpUrl, 1);
      } else {
        // Add new
        response = await addDestination(stream.stream_id, platform, rtmpUrl);
      }

      if (response.success) {
        // Refresh destinations
        const destRes = await getDestinations(stream.stream_id);
        if (destRes.success) {
          setDestinations(destRes.data || []);
        }
        setShowPlatformModal(false);
        setEditingDestination(null);
      } else {
        alert(response.error || "Failed to save platform connection");
      }
    } catch (error) {
      console.error("Save destination error:", error);
      alert("An unexpected error occurred");
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteDestination = async (destId) => {
    try {
      const response = await deleteDestination(destId);
      if (response.success) {
        const destRes = await getDestinations(stream.stream_id);
        if (destRes.success) {
          setDestinations(destRes.data || []);
        }
      } else {
        alert(response.error || "Failed to delete platform connection");
      }
    } catch (error) {
      console.error("Delete destination error:", error);
      alert("An unexpected error occurred");
    }
  };

  const handleCopyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    });
  };

  const parseJsonSafe = (data, fallback = []) => {
    if (!data) return fallback;
    if (typeof data === 'object') return data;
    try {
      return JSON.parse(data);
    } catch (e) {
      return fallback;
    }
  };

  const isStreamLive = streamStatus?.ivs?.state === 'LIVE';
  const streamIsReady = streamStatus?.stream?.status === 1;
  const hasMediaLiveRunning = mediaLiveDetails || streamStatus?.medialive?.state === 'RUNNING';

  if (loading) {
    return (
      <div className="crew-mngt-content p-6 flex justify-center items-center min-h-screen">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-button-coloured" />
          <p className="text-muted-foreground">Loading livestream...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="crew-mngt-content p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700 font-medium mb-2">Error Loading Livestream</p>
          <p className="text-red-600 text-sm mb-4">{error}</p>
          <Button onClick={() => navigate('/livestream')} className="bg-button-coloured">
            Back to All Livestreams
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="crew-mngt-content p-6">
      <Breadcrumbs />
      <div className="flex flex-wrap justify-between items-start mb-2">
        <PageTitle
          title="Live Streaming Control"
          description={`Manage your multi-platform livestream - ${project?.project_name || "N/A"}`}
        />

        <div className="flex gap-2">
          {hasMediaLiveRunning && (
            <Button
              className="bg-red-600 hover:bg-red-700 text-white rounded-lg py-2 mb-2 md:mb-0 flex items-center gap-2"
              onClick={handleStopStream}
              disabled={stopLoading}
            >
              {stopLoading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Stopping...</>
              ) : (
                <><X className="w-4 h-4" /> Stop Stream</>
              )}
            </Button>
          )}

          <Button
            className="bg-button-coloured rounded-lg py-2 mb-2 md:mb-0 flex items-center gap-2"
            onClick={handleGoLive}
            disabled={goLiveLoading || hasMediaLiveRunning}
          >
            {goLiveLoading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Starting...</>
            ) : (
              <><Radio className="w-4 h-4" /> Go Live</>
            )}
          </Button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-6 md:self-stretch">
        {/* Left Column */}
        <div className="md:w-3/4 flex flex-col justify-start items-start gap-6">
          {/* Stream Preview */}
          <div className="bg-card p-4 rounded-[14px] w-full">
            <p className="lg:text-lg font-semibold mb-6">Stream Preview - {project?.project_name}</p>

            {isStreamLive ? (
              // Show HLS player ONLY when IVS is LIVE
              <LivestreamPlayer
                playbackUrl={stream?.playback_url}
                isLive={true}
                className="h-[387px] rounded-[14px]"
              />
            ) : hasMediaLiveRunning ? (
              // Waiting for OBS (MediaLive Running but IVS not Live)
              <div className="h-[387px] bg-black rounded-[14px] flex flex-col items-center justify-center text-white relative p-6 text-center">
                <Loader2 className="w-10 h-10 animate-spin text-button-coloured mb-4" />
                <p className="text-lg font-medium">Waiting for OBS to start streaming...</p>
                <p className="text-sm opacity-70 mt-2 max-w-md">
                  MediaLive is running. Please start streaming from your OBS/Encoder software to go live.
                </p>
              </div>
            ) : (
              // Default Offline Placeholder
              <div className="h-[387px] bg-black rounded-[14px] flex flex-col items-center justify-center text-white relative p-6 text-center">
                {stream?.playback_url ? (
                  <p className="text-sm opacity-50 px-4">
                    Video Player Placeholder
                    <br />
                    <span className="text-xs truncate max-w-md block mt-2">Waiting for live stream...</span>
                  </p>
                ) : (
                  <p className="text-sm opacity-50">Preview not available</p>
                )}
              </div>
            )}

            {/* Metrics */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mt-6">
              <div className="bg-popover p-4 rounded-[14px] flex flex-col gap-2 text-sm">
                <span className="text-popover-foreground font-semibold">Bitrate</span>
                <span className="text-[#737373]">
                  {streamHealth?.networkKbps ? `${streamHealth.networkKbps} kbps` : '--'}
                </span>
              </div>
              <div className="bg-popover p-4 rounded-[14px] flex flex-col gap-2 text-sm">
                <span className="text-popover-foreground font-semibold">FPS</span>
                <span className="text-[#737373]">
                  {streamHealth?.fps || '--'}
                </span>
              </div>
              <div className="bg-popover p-4 rounded-[14px] flex flex-col gap-2 text-sm">
                <span className="text-popover-foreground font-semibold">Resolution</span>
                <span className="text-[#737373]">
                  {streamHealth?.resolution || '1920x1080'}
                </span>
              </div>
              <div className="bg-popover p-4 rounded-[14px] flex flex-col gap-2 text-sm">
                <span className="text-popover-foreground font-semibold">Viewers</span>
                <span className="text-[#737373]">
                  {streamHealth?.ivs?.viewerCount || 0}
                </span>
              </div>
            </div>
          </div>

          {/* OBS / External Encoder Settings */}
          {mediaLiveDetails?.input?.Destinations && (
            <div className="bg-card p-4 rounded-[14px] w-full">
              <div className="flex justify-between items-center mb-6">
                <p className="lg:text-lg font-semibold">OBS / External Encoder Settings</p>
                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                  MediaLive Running
                </span>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 flex gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium mb-1">Configure your encoder (OBS/FFmpeg/etc.)</p>
                  <p className="text-xs">Copy one of the RTMP URLs below as your server. Start streaming from your encoder to begin broadcasting.</p>
                </div>
              </div>

              <div className="flex flex-col gap-6">
                {mediaLiveDetails.input.Destinations.map((dest, index) => {
                  const { server, key } = parseRtmpUrl(dest.Url);
                  return (
                    <div key={index} className="flex flex-col gap-3">
                      <p className="text-sm font-medium text-muted-foreground">Endpoint {index + 1}</p>

                      {/* Server URL */}
                      <div className="bg-popover p-3 rounded-[14px] flex items-center justify-between gap-2 border border-border/50">
                        <div className="flex-1 min-w-0">
                          <code className="text-sm font-mono text-foreground break-all">
                            {server}
                          </code>
                        </div>
                        <Button
                          onClick={() => handleCopyToClipboard(server, `server-${index}`)}
                          className="bg-black text-white hover:bg-black/90 flex-shrink-0 h-9 px-4 rounded-lg"
                          size="sm"
                        >
                          {copiedIndex === `server-${index}` ? (
                            <><Check className="w-4 h-4 mr-1" /> Copied</>
                          ) : (
                            <><Copy className="w-4 h-4 mr-1" /> Copy</>
                          )}
                        </Button>
                      </div>

                      {/* Stream Key */}
                      <div className="bg-popover p-3 rounded-[14px] flex items-center justify-between gap-2 border border-border/50">
                        <div className="flex-1 min-w-0">
                          <code className="text-sm font-mono text-foreground break-all">
                            {key}
                          </code>
                        </div>
                        <Button
                          onClick={() => handleCopyToClipboard(key, `key-${index}`)}
                          className="bg-black text-white hover:bg-black/90 flex-shrink-0 h-9 px-4 rounded-lg"
                          size="sm"
                        >
                          {copiedIndex === `key-${index}` ? (
                            <><Check className="w-4 h-4 mr-1" /> Copied</>
                          ) : (
                            <><Copy className="w-4 h-4 mr-1" /> Copy</>
                          )}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
                <p className="font-medium mb-1">OBS Setup Instructions:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Open OBS → Settings → Stream</li>
                  <li>Service: Custom</li>
                  <li>Server: Paste one of the RTMP URLs above</li>
                  <li>Stream Key: Leave empty</li>
                  <li>Click "Start Streaming" in OBS</li>
                </ol>
              </div>
            </div>
          )}

          {/* Stream Health */}
          <div className="bg-card p-4 rounded-[14px] w-full">
            <p className="mb-6 flex gap-2 items-center">
              <Activity className="w-5 h-5 text-orange-600" />
              Stream Health
            </p>
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-sm">Connection Quality</span>
                <span className="text-[#57534D] text-sm">
                  {streamHealth?.connectionQualityPercent ?? (isStreamLive ? '95' : '0')} %
                </span>
              </div>
              <Progress
                value={streamHealth?.connectionQualityPercent ?? (isStreamLive ? 95 : 0)}
                className="w-full h-2 rounded-full bg-[#030213]/20 [&>div]:bg-[#030213] [&>div]:rounded-full"
              />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
              <div className={`${isStreamLive ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-300'} border p-4 rounded-[14px] flex flex-col gap-2`}>
                <span className="flex gap-2 items-center text-xs">
                  <Wifi className={`w-4 h-4 ${isStreamLive ? 'text-green-600' : 'text-gray-400'}`} />
                  Network
                </span>
                <span className={isStreamLive ? 'text-green-600' : 'text-gray-500'}>
                  {streamHealth?.networkKbps || '--'} kbps
                </span>
              </div>
              <div className={`${isStreamLive ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-300'} border p-4 rounded-[14px] flex flex-col gap-2`}>
                <span className="flex gap-2 items-center text-xs">
                  <Activity className={`w-4 h-4 ${isStreamLive ? 'text-green-600' : 'text-gray-400'}`} />
                  CPU
                </span>
                <span className={isStreamLive ? 'text-green-600' : 'text-gray-500'}>
                  {streamHealth?.cpu || '--'}
                </span>
              </div>
              <div className={`${hasMediaLiveRunning ? 'bg-green-50 border-green-300' : 'bg-amber-50 border-amber-300'} border p-4 rounded-[14px] flex flex-col gap-2`}>
                <span className="flex gap-2 items-center text-xs">
                  <Monitor className={`w-4 h-4 ${hasMediaLiveRunning ? 'text-green-600' : 'text-amber-600'}`} />
                  Encoding
                </span>
                <span className={hasMediaLiveRunning ? 'text-green-600' : 'text-amber-700'}>
                  {streamHealth?.encodingStatus || streamStatus?.medialive?.state || 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="md:w-1/4 flex flex-col justify-start items-start gap-6">
          {/* Platform Connections */}
          <div className="bg-card p-4 rounded-[14px] w-full">
            <p className="lg:text-lg font-semibold mb-6">Platform Connections</p>
            <div className="flex flex-col gap-4">
              {destinations.map((dest) => (
                <div key={dest.stream_destination_id} className="flex flex-col gap-4 p-4 rounded-[14px] bg-white border border-border/50 shadow-sm relative">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${dest.is_active ? "bg-[#22C55E]" : "bg-gray-300"}`} />
                      <p className="font-medium text-[#111827] text-base">{platformNames[dest.platform] || dest.platform}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleDeleteDestination(dest.stream_destination_id)}
                        className="text-muted-foreground hover:text-red-500 transition-colors p-1"
                        title="Remove Destination"
                        disabled={hasMediaLiveRunning}
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <Switch
                        checked={!!dest.is_active}
                        onCheckedChange={(checked) => {
                          handleSaveDestination({
                            platform: dest.platform,
                            rtmpUrl: dest.rtmp_url,
                            destinationId: dest.stream_destination_id,
                            isActive: checked ? 1 : 0
                          });
                        }}
                        className="data-[state=checked]:bg-[#111827]"
                      />
                    </div>
                  </div>
                  <div className="flex justify-between items-center bg-transparent">
                    <span className="text-sm text-[#6B7280] font-normal">Status</span>
                    <span className={`text-xs font-medium px-3 py-1 rounded-full ${dest.is_active ? 'text-[#15803D] bg-[#DCFCE7]' : 'text-gray-600 bg-gray-100'}`}>
                      {dest.is_active ? 'ready' : 'inactive'}
                    </span>
                  </div>
                </div>
              ))}

              <Button
                className="bg-button-coloured px-6 py-2"
                onClick={() => {
                  setEditingDestination(null);
                  setShowPlatformModal(true);
                }}
                disabled={hasMediaLiveRunning}
              >
                Add Platform
              </Button>

              {hasMediaLiveRunning && (
                <p className="text-xs text-amber-600 text-center mt-2">
                  Stop stream to modify platforms
                </p>
              )}
            </div>
          </div>

          {/* Quick Controls */}
          <div className="bg-card p-4 rounded-[14px] w-full">
            <p className="lg:text-lg font-semibold mb-6">Quick Controls</p>
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <p className="font-medium text-sm">Stream Quality</p>
                <Select
                  options={streamQualityOptions}
                  value={selectedQuality || project?.stream_quality}
                  onChange={setSelectedQuality}
                  placeholder="Select Quality"
                />
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">Audio Level</span>
                  <span className="text-[#57534D] text-sm">{sliderValue} %</span>
                </div>
                <Slider
                  value={sliderValue}
                  onValueChange={setSliderValue}
                  max={100}
                  step={1}
                  trackClassName="bg-accent"
                  rangeClassName="bg-black"
                  className="w-full py-4"
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">Microphone</span>
                <Switch
                  checked={microphoneOn}
                  onCheckedChange={setMicrophoneOn}
                  className="data-[state=checked]:bg-button-coloured data-[state=unchecked]:bg-accent"
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">Camera</span>
                <Switch
                  checked={cameraOn}
                  onCheckedChange={setCameraOn}
                  className="data-[state=checked]:bg-button-coloured data-[state=unchecked]:bg-accent"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Platform Connection Modal */}
      <PlatformConnectionModal
        isOpen={showPlatformModal}
        onClose={() => {
          setShowPlatformModal(false);
          setEditingDestination(null);
        }}
        onSave={handleSaveDestination}
        destination={editingDestination}
        loading={modalLoading}
      />
    </div>
  );
};

export default ManageLivestreamPage;
